/**
 * Authentication Gate Component
 * Orchestrates auth flow: First Run → Setup Wizard → Login → App
 * Phase 2: Multi-User Auth Foundation
 *
 * SUPABASE INTEGRATION (2025-01-21):
 * - Now checks Supabase session first (if configured)
 * - Falls back to localStorage auth if Supabase not available
 * - Bridges Supabase user to RBAC system
 *
 * Usage: Wrap your app with <AuthGate>...</AuthGate>
 * When auth is disabled, children render immediately.
 * When auth is enabled:
 *   - First run: Shows SetupWizard
 *   - No session: Shows LoginScreen
 *   - Authenticated: Renders children
 */

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  isFirstRun,
  isAuthEnabled,
  getCurrentSession,
  getCurrentUser,
  logout as authLogout,
  extendSession,
  ROLES,
  setRBACUser
} from '../../../platform/auth/authService';
import { SetupWizard } from './SetupWizard';
import { LoginScreen } from './LoginScreen';
import { ResetPasswordScreen } from './ResetPasswordScreen';
import { Ship, Loader2, AlertCircle } from 'lucide-react';

// Azure auth integration
import { isAzureApiConfigured, azureApiClient, resetLogoutInFlight } from '../../../lib/azureApiClient';

// Logging utility
import { devLog, devWarn, devError } from '../../../utils/devLog';

// FT-223: Vessel reconciliation imports
import { legacyGet, legacySet } from '../../../storage/legacyUtils';
import { cleanupVesselStorage } from '../../../storage/migrations/migrateToMultiVessel';
// FT-365 v3.1 hotfix: self-healing raw reader for currentVesselId (unwraps
// legacy double-JSON encoding from a brief FT-365 v3 exposure).
import { readCurrentVesselIdRaw } from '../../../utils/readCurrentVesselId';

// FT-365 v2: cross-user cache wipe + confirmation dialog + outbox preflight
import { wipeUserCaches } from '../../../lib/authCacheWipe';
// FT-469: explicit belt-and-braces clear of stale working-org on logout.
import { clearWorkingOrg } from '../../../utils/getOrgId';
import { listOutbox, processOutbox } from '../../../lib/outbox';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { azureCloudClient } from '../../../sync/azureCloudClient';
// FT-365 Option B: sole sanctioned dispatcher of sr:auth:userChanged.
// Handles LS.lastUserId write + event dispatch + (flag-gated) atomic reload.
// See feedback_auth_boundary_reload_invariant.md.
import { dispatchUserBoundaryReload, clearBoundaryReloadFuse } from '../../../lib/authBoundary';

const LAST_USER_ID_KEY = 'sr.auth.lastUserId';

// Auth context for accessing auth state throughout the app
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthGate');
  }
  return context;
};

// Loading screen while checking auth state
const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center">
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4">
        <Ship className="w-8 h-8 text-white" />
      </div>
      <div className="flex items-center justify-center gap-2 text-gray-600">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Loading...</span>
      </div>
    </div>
  </div>
);

// WEEK 4: Screen shown when user is authenticated but not in any organization
const NoOrganizationScreen = ({ user, onLogout }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
    <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md text-center border border-gray-100">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
        <AlertCircle className="w-8 h-8 text-amber-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">No Organization Access</h2>
      <p className="text-gray-600 mb-6">
        Your account <span className="font-medium">{user?.email}</span> exists but isn't assigned to any organization yet.
      </p>
      <p className="text-sm text-gray-500 mb-6">
        Please contact your administrator to be added to an organization, or wait for your invitation to be processed.
      </p>
      <button
        onClick={onLogout}
        className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
      >
        Sign out
      </button>
    </div>
  </div>
);

export const AuthGate = ({ children, enabled = null }) => {
  const [loading, setLoading] = useState(true);
  const [authState, setAuthState] = useState({
    firstRun: false,
    authEnabled: false,
    session: null,
    user: null,
    noOrganization: false, // Track if user has no org membership
    showResetPasswordScreen: false // Track if showing password reset screen
  });

  // FT-365 v2: confirmation dialog for offline logout with queued outbox items.
  const { showConfirm, ConfirmDialog } = useCustomDialogs();

  // Check auth state on mount and when relevant
  // WEEK 4 REMEDIATION: When Supabase is configured, skip localStorage entirely
  // WEEK 4 UPDATE: toAppUser is now async and fetches organization from database
  const checkAuthState = useCallback(async () => {
    // FT-365 v3.5: signal initial auth+vessels hydration complete on the way out.
    // VesselContext listens for sr:auth:initialHydrated to suppress the "Add
    // your first vessel" welcome-gate flash during the account-change window
    // between Layer B's wipeUserCaches and getVessels() resolving. Wrapped in
    // try/finally so the event fires on every exit path (success, no-session,
    // catch, no-org, localStorage fallback) — never leave the UI stuck
    // hydrating. Non-fatal on failure.
    try {
    // When Azure is configured, we don't use localStorage auth at all
    // So firstRun (which checks localStorage) is irrelevant
    const firstRun = isAzureApiConfigured() ? false : isFirstRun();

    // When Azure is configured, auth is always enabled
    const authEnabled = isAzureApiConfigured() ? true : (enabled !== null ? enabled : isAuthEnabled());

    // Try Azure auth first if configured
    if (isAzureApiConfigured()) {
      try {
        const { user } = await azureApiClient.getSession();
        if (user) {
          // FT-365 v2 Layer B: user-boundary check. If the session belongs to
          // a DIFFERENT user than the last one recorded on this device, wipe
          // caches BEFORE any downstream code reads them. Direct localStorage
          // is required — this IS the pre-hydration boundary check.
          try {
            // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 boundary marker (LS is intentional)
            const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
            if (lastUserId && lastUserId !== user.id) {
              devWarn('[AuthGate] FT-365 Layer B: user-boundary mismatch, wiping caches', {
                lastUserId,
                currentUserId: user.id,
              });
              // FT-365 v3 R2 blocker 6: Layer B (user-boundary mismatch) is a
              // different user on this device — drop the SW too so /api/*
              // workbox-cached responses can't leak across the boundary.
              // FT-365 v3.4: preserveAuth restored (was in FT-365d which was
              // reverted alongside FT-365 v2). Without it, the wipe here nukes
              // the fresh JWT that LoginScreen just wrote seconds ago, causing
              // the immediate getVessels() call to 401 and bounce the user to
              // LoginScreen. Only happens on account change (user A → user B)
              // because that's when Layer B fires.
              await wipeUserCaches({
                preservePhotos: true,
                preserveOutbox: true,
                unregisterSW: true,
                preserveAuth: true,
              });
              // FT-365 Option B (risk #2 BLOCKER fix): the sr:auth:userChanged
              // dispatch and LS.lastUserId write MUST live INSIDE the mismatch
              // branch. In v3.5.1 they were outside — they fired on every
              // checkAuthState invocation (same-user refresh included) and
              // were only harmless because Layer D's next===prev short-circuit
              // masked them. Under Option B the helper reloads the page, so
              // an unconditional dispatch here would cause an infinite reload
              // loop on every refresh. See risk-#2 verification
              // (a7538fbb87f71bc13.output) and feedback_auth_boundary_reload_invariant.md.
              // Helper writes LAST_USER_ID_KEY internally, dispatches
              // sr:auth:userChanged, and (flag-gated) reloads the page.
              dispatchUserBoundaryReload(user.id);
            } else if (!lastUserId) {
              // First-login baseline: no previous marker exists, so there is no
              // boundary to reload across. Silently write the marker so future
              // account changes can be detected. NO dispatch, NO reload — this
              // is the "no-op" path that must never trigger the helper.
              // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 boundary marker (LS is intentional)
              localStorage.setItem(LAST_USER_ID_KEY, user.id);
            }
            // Same-user refresh (lastUserId === user.id): both branches above
            // skipped. LS already correct, no dispatch, no reload. This is the
            // hot path — must remain side-effect-free per Option B invariant.

            // FT-365 Option B HARDENING: successful boundary resolution — we
            // reached checkAuthState and did NOT dispatch a reload (either
            // same-user refresh or first-login baseline, i.e. the non-mismatch
            // paths). Clear the reload-loop fuse counter so a normal sequence of
            // account switches within one session never falsely trips it. The
            // fuse only exists to catch a reload that FAILS to resolve; landing
            // here IS the "resolved" signal.
            if (!lastUserId || lastUserId === user.id) {
              clearBoundaryReloadFuse();
            }
          } catch (err) {
            devWarn('[AuthGate] FT-365 Layer B check failed (non-fatal):', err);
          }

          // FT-168 FIX: ALWAYS bridge to RBAC system first, regardless of organizationId
          // This ensures current_user is always written to localStorage
          setRBACUser(user);

          // FT-223: Reconcile vessels on session restore
          // Server may have deleted vessels since last login — remove stale data
          try {
            const vesselResponse = await azureApiClient.getVessels();
            // API returns { success: true, vessels: [...] } not a plain array
            const serverVessels = vesselResponse?.vessels || (Array.isArray(vesselResponse) ? vesselResponse : []);

            // GUARD: Only reconcile if server returned valid vessel array
            // Empty response must NOT wipe local vessels (could be API error or offline)
            if (Array.isArray(serverVessels) && serverVessels.length > 0) {
              const serverVesselIds = new Set(serverVessels.map(v => v.id));

              // Get current localStorage vessels
              const localVessels = legacyGet(null, 'vessels') || [];

              // Find vessels in localStorage but not on server (deleted server-side)
              const deletedVesselIds = localVessels
                .filter(v => v && v.id && !serverVesselIds.has(v.id))
                .map(v => v.id);

              // FT-302 (H3a): if local vessels are EMPTY but server has vessels,
              // the LS key has been evicted (Android storage pressure / cleared
              // site data / bfcache miss). Previously we only wrote server-side
              // vessels back into LS on the *deletion* path — meaning an evicted
              // LS would leave `vessels === []` after mount, hard-sticking the
              // "Add Your First Vessel" gate across every subsequent refresh.
              // Now we also restore on empty-local, and unify with the deletion
              // path so any mismatch (missing OR count-different) resyncs LS.
              const localMissing = localVessels.length === 0;
              const countMismatch = localVessels.length !== serverVessels.length;
              const needsResync = localMissing || countMismatch || deletedVesselIds.length > 0;

              if (needsResync) {
                if (deletedVesselIds.length > 0) {
                  devLog(`[AuthGate] FT-223: Removing ${deletedVesselIds.length} deleted vessel(s) from localStorage`);
                  deletedVesselIds.forEach(vesselId => {
                    cleanupVesselStorage(vesselId);
                  });
                }

                if (localMissing) {
                  devLog(`[AuthGate] FT-302: local vessels empty but server has ${serverVessels.length}, restoring from server`);
                }

                // Update vessels list to match server
                legacySet(null, 'vessels', serverVessels);

                // If current vessel was deleted, switch to first available
                const currentVesselId = readCurrentVesselIdRaw();
                if (currentVesselId && deletedVesselIds.includes(currentVesselId)) {
                  const newCurrentId = serverVessels[0]?.id || null;
                  // FT-365 v3.1 hotfix: write raw (not legacySet — see helper
                  // header for full context on the double-encoding freeze).
                  // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3.1 raw write matches FT-64 vesselGuard raw read
                  localStorage.setItem('currentVesselId', newCurrentId || '');
                  devLog(`[AuthGate] FT-223: Current vessel ${currentVesselId} was deleted, switched to ${newCurrentId}`);
                }
              }

              // FT-365 v3 Layer A#1: always reconcile currentVesselId, not just
              // on the deleted-branch. Otherwise every non-deleted path leaves
              // currentVesselId at whatever the previous user had, VesselContext
              // falls back to vessels[0], and super_admin's synthetic org bleeds
              // Ryan's vessels (or vice versa) into the header + switcher.
              // Priority: preferredVesselId → previous currentVesselId (if still valid)
              //           → serverVessels[0] → null + showVesselPickerGate.
              try {
                // FT-365 v3.1: read via helper so any legacy double-encoded
                // value from a brief v3 exposure self-heals on read.
                const previousCurrentVesselId = readCurrentVesselIdRaw();
                const validServerIds = new Set(serverVessels.map((v) => v && v.id).filter(Boolean));
                let restoredId = null;
                let restoredSource = null;
                if (user.preferredVesselId && validServerIds.has(user.preferredVesselId)) {
                  restoredId = user.preferredVesselId;
                  restoredSource = 'preferredVesselId';
                } else if (previousCurrentVesselId && validServerIds.has(previousCurrentVesselId)) {
                  restoredId = previousCurrentVesselId;
                  restoredSource = 'previousCurrentVesselId';
                } else if (serverVessels[0] && serverVessels[0].id) {
                  restoredId = serverVessels[0].id;
                  restoredSource = 'serverVessels[0]';
                }

                if (restoredId) {
                  // FT-365 v3.1 hotfix: raw write (not legacySet). Downstream
                  // readers use raw LS reads; double-encoding via legacySet
                  // caused the FT-64 vessel-guard storm freeze.
                  // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3.1 raw write matches FT-64 vesselGuard raw read
                  localStorage.setItem('currentVesselId', restoredId);
                  // Clear any stale picker-gate flag left by a prior mount.
                  // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3 Layer A#1 boundary write
                  try { localStorage.removeItem('sr.vessel.showPickerGate'); } catch (_) { /* non-fatal */ }
                  // FT-365 v3 R2 blocker 8: notify same-tab VesselPickerModal
                  // that the gate flag flipped (storage event only fires cross-tab).
                  try {
                    window.dispatchEvent(new CustomEvent('sr:vessel:pickerGate:changed', { detail: { showGate: false } }));
                  } catch (_) { /* non-fatal */ }
                  devLog(`[AuthGate] FT-365 v3 Layer A#1: reconciled currentVesselId via ${restoredSource}: ${restoredId}`);
                } else {
                  // All three priorities failed. Set a picker-gate flag so the
                  // UI renders a chooser instead of falling back to VesselContext
                  // React state (which may still hold the previous user's data).
                  try {
                    // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3 Layer A#1 boundary write
                    localStorage.removeItem('currentVesselId');
                    // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3 Layer A#1 boundary write
                    localStorage.setItem('sr.vessel.showPickerGate', 'true');
                  } catch (_) { /* non-fatal */ }
                  // FT-365 v3 R2 blocker 8: notify same-tab VesselPickerModal.
                  try {
                    window.dispatchEvent(new CustomEvent('sr:vessel:pickerGate:changed', { detail: { showGate: true } }));
                  } catch (_) { /* non-fatal */ }
                  devWarn('[AuthGate] FT-365 v3 Layer A#1: no valid vessel to restore, showing picker gate');
                }
              } catch (reconcileErr) {
                devWarn('[AuthGate] FT-365 v3 Layer A#1: reconcile failed (non-fatal):', reconcileErr);
              }
            }
          } catch (error) {
            devWarn('[AuthGate] FT-223: Vessel reconciliation failed (offline?):', error.message);
            // Continue with cached vessels if reconciliation fails (offline mode)
          }

          // Check organization membership (handle both 'super_admin' and 'SUPER_ADMIN')
          const isSuperAdmin = user.role?.toUpperCase() === 'SUPER_ADMIN';

          if (!user.organizationId && !isSuperAdmin) {
            devWarn('[AuthGate] User authenticated but not in any organization');
            setAuthState({
              firstRun,
              authEnabled,
              session: {
                userId: user.id,
                token: azureApiClient.getToken(),
                role: user.role,
                userName: user.name,
                userEmail: user.email,
                expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000),
                isAzure: true
              },
              user: user,
              noOrganization: true
            });
            setLoading(false);
            return;
          }

          // RBAC bridge already called above

          setAuthState({
            firstRun,
            authEnabled,
            session: {
              userId: user.id,
              token: azureApiClient.getToken(),
              role: user.role,
              userName: user.name,
              userEmail: user.email,
              expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000),
              isAzure: true
            },
            user: user,
            noOrganization: false
          });
          setLoading(false);
          return;
        }
      } catch (error) {
        devError('[AuthGate] Azure auth check failed:', error);
        setAuthState({
          firstRun: false,
          authEnabled: true,
          session: null,
          user: null,
          noOrganization: false
        });
        setLoading(false);
        return;
      }

      // Azure configured but no session - show login
      setAuthState({
        firstRun: false,
        authEnabled: true,
        session: null,
        user: null,
        noOrganization: false
      });
      setLoading(false);
      return;
    }

    // localStorage auth ONLY when Supabase is NOT configured (dev/offline mode)
    const session = getCurrentSession();
    const user = session ? getCurrentUser() : null;

    // Bridge to RBAC on session restore (page refresh, browser reopen)
    if (user) {
      setRBACUser(user);
    }

    setAuthState({
      firstRun,
      authEnabled,
      session,
      user,
      noOrganization: false
    });
    setLoading(false);
    } finally {
      // FT-365 v3.5: fire the initial-hydration signal on EVERY exit path
      // (both success and catch). VesselContext flips isHydrating→false so
      // TailwindShell can render the welcome gate (or normal shell) without
      // the transient empty-vessels flash. Non-fatal on dispatch failure.
      try {
        window.dispatchEvent(new CustomEvent('sr:auth:initialHydrated'));
      } catch (_) { /* non-fatal */ }
    }
  }, [enabled]);

  useEffect(() => {
    // Check for recovery token in URL hash FIRST
    const hash = window.location.hash;
    if (hash.includes('type=recovery') && hash.includes('access_token=')) {
      devLog('[AuthGate] Password recovery token detected in URL');
      setAuthState(prev => ({
        ...prev,
        showResetPasswordScreen: true,
        authEnabled: true
      }));
      setLoading(false);
      return; // Skip normal auth check
    }

    checkAuthState();

    // Azure JWT tokens don't have real-time auth events
    // Session validation happens on mount (above) and on API 401 errors
    // No listener needed - tokens are validated when used
  }, [checkAuthState]);

  // Extend session on user activity (every 5 minutes if active)
  // Only for localStorage sessions - Azure handles its own token refresh
  useEffect(() => {
    if (!authState.session || authState.session.isAzure) return;

    const interval = setInterval(() => {
      extendSession();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [authState.session]);

  // Handle setup completion
  const handleSetupComplete = useCallback(() => {
    checkAuthState();
  }, [checkAuthState]);

  // Handle login success
  const handleLoginSuccess = useCallback((user, _session) => {
    // Bridge to RBAC immediately on login
    setRBACUser(user);

    // FT-365c: do NOT write LAST_USER_ID_KEY here. Adversarial review (FT-365c)
    // caught that writing it here defeats Layer B entirely — the fresh mount
    // post-reload would always see a matching marker and skip the wipe. The
    // marker is written in checkAuthState AFTER the mismatch-check-and-wipe,
    // which is the only correct ordering. Per §13.4 (corrected).

    // FT-365 v3.3: DO NOT setAuthState here. window.location.reload() below
    // will re-mount the entire app fresh from LS. Any state update before the
    // reload triggers a wasted rerender + a duplicate checkAuthState fire
    // (VesselProviderKeyedByAuth's key={userKey} transitions empty → user.id,
    // remounting the subtree and firing checkAuthState twice ~100-140ms apart
    // in prod). Doubled /auth/verify calls doubled the probability of a
    // transient 401 (replica cold-start / clock skew / eviction) triggering
    // the sr:auth:expired wipe-and-reload, bouncing the user back to login.
    // Reload alone is sufficient — see FT-365 v3.3 investigation.
    window.location.reload();
  }, []);

  // Handle reset completion
  const handleResetComplete = useCallback(async () => {
    // Sign out the recovery session
    if (isAzureApiConfigured()) {
      azureApiClient.logout();
    }

    // Clear state and show login
    setAuthState(prev => ({
      ...prev,
      showResetPasswordScreen: false,
      session: null,
      user: null
    }));
  }, []);

  // FT-365 v2 logout (Layer A) — see DESIGN-BRIEF §4. Async preflight (outbox
  // check + optional confirm) → clear auth → wipeUserCaches → reload. The
  // reload tears down VesselProvider + all useStorageState closures.
  const logout = useCallback(async () => {
    try {
      let outboxItems = [];
      try { outboxItems = await listOutbox(); } catch (err) {
        devWarn('[AuthGate] FT-365: outbox read failed (non-fatal):', err);
      }
      const pendingCount = Array.isArray(outboxItems)
        ? outboxItems.filter((i) => i && i.status === 'pending').length : 0;
      const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

      if (pendingCount > 0) {
        if (isOnline) {
          // 3s flush window — queue survives on preserve whitelist either way.
          try {
            const flushResult = await Promise.race([
              processOutbox(azureCloudClient),
              new Promise((r) => setTimeout(() => r({ timedOut: true }), 3000)),
            ]);
            if (flushResult && flushResult.timedOut) {
              devWarn('[AuthGate] FT-365: outbox flush timed out, queue preserved');
            }
          } catch (err) {
            devWarn('[AuthGate] FT-365: outbox flush failed (non-fatal):', err);
          }
        } else {
          const confirmed = await showConfirm(
            'Sign out with unsynced changes?',
            `You have ${pendingCount} unsynced change${pendingCount === 1 ? '' : 's'} that will remain queued on this device and flush when the same user signs in with connectivity.`,
            'Sign out and preserve queue',
            'Cancel',
            'warning'
          );
          if (!confirmed) return; // Abort logout
        }
      }

      if (isAzureApiConfigured()) azureApiClient.logout();
      authLogout();

      // FT-469 (cross-org isolation): explicitly drop any working-org so it can
      // never linger past sign-out. wipeUserCaches also clears it (AUTH_KEYS),
      // but this guarantees the clear even if the wipe is skipped/fails below.
      try { clearWorkingOrg(); } catch { /* non-fatal */ }

      try {
        await wipeUserCaches({ preservePhotos: true, preserveOutbox: true, offline: !isOnline });
      } catch (err) {
        devWarn('[AuthGate] FT-365: wipeUserCaches failed (non-fatal, continuing):', err);
      }

      window.location.reload();
    } catch (err) {
      devError('[AuthGate] FT-365: logout flow crashed, forcing reload:', err);
      // FT-365c: reset the Layer C debounce flag before attempting the last-
      // ditch reload. If the reload itself throws, we're already crashed —
      // but resetting first means any surviving code path can still fire
      // sr:auth:expired later without being silently muted.
      try { resetLogoutInFlight(); } catch { /* non-fatal */ }
      try { window.location.reload(); } catch { /* nothing more we can do */ }
    }
  }, [showConfirm]);

  // FT-365 v2 Layer C: unified 401 auto-logout via `sr:auth:expired` event.
  // apiRequest() (azureApiClient.js) dispatches this event on 401 with a
  // module-scope in-flight debounce; AuthGate calls the SAME logout flow so
  // 401-driven signouts get the full wipe + reload treatment.
  useEffect(() => {
    const handler = () => {
      devLog('[AuthGate] FT-365 Layer C: sr:auth:expired received, running logout');
      logout();
    };
    window.addEventListener('sr:auth:expired', handler);
    return () => window.removeEventListener('sr:auth:expired', handler);
  }, [logout]);

  // Role check helper
  const hasRole = useCallback((requiredRole) => {
    if (!authState.user) return false;

    const roleHierarchy = {
      [ROLES.ADMIN]: 4,
      [ROLES.MASTER]: 3,
      [ROLES.OFFICER]: 2,
      [ROLES.CREW]: 1
    };

    const userLevel = roleHierarchy[authState.user.role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;

    return userLevel >= requiredLevel;
  }, [authState.user]);

  // Context value
  const contextValue = {
    user: authState.user,
    session: authState.session,
    isAuthenticated: !!authState.session,
    authEnabled: authState.authEnabled,
    logout,
    hasRole,
    refreshAuth: checkAuthState,
    ROLES
  };

  // Loading state
  if (loading) {
    return <LoadingScreen />;
  }

  // Check if auth should be enforced
  // If enabled prop is explicitly false, skip auth entirely
  // If enabled prop is null/undefined, use authEnabled from config
  const shouldEnforceAuth = enabled === false ? false : authState.authEnabled;

  // If auth is not enabled, render children directly with minimal context
  if (!shouldEnforceAuth) {
    return (
      <AuthContext.Provider value={{
        user: null,
        session: null,
        isAuthenticated: false,
        authEnabled: false,
        logout: () => {},
        hasRole: () => true, // Allow all when auth disabled
        refreshAuth: checkAuthState,
        ROLES
      }}>
        {children}
      </AuthContext.Provider>
    );
  }

  // FT-365 v2: the ConfirmDialog must render alongside every branch that may
  // trigger `showConfirm` from the logout preflight (i.e. every branch that
  // exposes logout via context OR renders NoOrganizationScreen with onLogout).
  // Fragment-wrap so it's always present in the tree.

  // First run - show setup wizard
  // Skip SetupWizard entirely when Azure is configured
  // Users are provisioned via Admin Panel, not SetupWizard
  if (authState.firstRun && !isAzureApiConfigured()) {
    return (
      <>
        <SetupWizard onComplete={handleSetupComplete} />
        <ConfirmDialog />
      </>
    );
  }

  // Password reset flow - show reset screen
  if (authState.showResetPasswordScreen) {
    return (
      <>
        <ResetPasswordScreen onResetComplete={handleResetComplete} />
        <ConfirmDialog />
      </>
    );
  }

  // No session - show login
  if (!authState.session) {
    return (
      <>
        <LoginScreen onLoginSuccess={handleLoginSuccess} />
        <ConfirmDialog />
      </>
    );
  }

  // User authenticated via Azure but not in any organization
  if (authState.noOrganization && authState.session?.isAzure) {
    return (
      <>
        <NoOrganizationScreen user={authState.user} onLogout={logout} />
        <ConfirmDialog />
      </>
    );
  }

  // Authenticated - render children with auth context
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog />
    </AuthContext.Provider>
  );
};

export default AuthGate;
